import { CoachOverviewClient } from "../lib/queries";

/**
 * Where this athlete's subscription actually stands, in one place, for both of the coach's screens.
 *
 * They disagreed. Immediately after a family cancelled, the clients list said "Leaving on September 7"
 * while the client page said "Billing active" and "Moving to Maintenance on September 7", with the
 * cancellation not shown at all. So the coach taps the card *because* it says leaving, lands on the page
 * that should explain it, and concludes they are staying (UX review F41).
 *
 * They disagreed because each screen rendered whichever fields it happened to have: the list knew about
 * `cancel_at`, the client page knew about the pending plan, and neither knew about the other. One
 * component reading both is the fix, and it is also what F40 asks for -- the *sequence* rather than the
 * end state, because "they moved to your cheapest tier and then cancelled" is the most actionable
 * retention signal a coach will ever get, and from his side it read as simply quitting.
 */
export interface SubscriptionSequence {
  /** A cheaper plan they have chosen, waiting for the month they have paid for to end. */
  pendingPlanName?: string | null;
  pendingEffectiveAt?: string | null;
}

export function describeSubscriptionSequence(
  client: Pick<CoachOverviewClient, "billing_status" | "cancel_at" | "pending_inactive_at" | "status">,
  sequence: SubscriptionSequence = {}
): string[] {
  const lines: string[] = [];

  // Order matters: this is a sequence, and the downgrade happens before the cancellation it precedes.
  if (sequence.pendingPlanName && sequence.pendingEffectiveAt) {
    lines.push(`Moving to ${sequence.pendingPlanName} on ${formatDate(sequence.pendingEffectiveAt)}`);
  }

  if (client.billing_status === "cancelled") {
    lines.push("Subscription ended");
  } else if (client.cancel_at) {
    lines.push(`Leaving on ${formatDate(client.cancel_at)}`);
  }

  if (client.pending_inactive_at && client.status !== "inactive") {
    lines.push(`Stopping on ${formatDate(client.pending_inactive_at)}`);
  }

  // Past due is deliberately NOT here. The client page already renders it from `billing_status`, and
  // this component is about the sequence of things that are going to happen -- a downgrade, a departure
  // -- rather than the plain current status it sits beside.

  return lines;
}

/**
 * The badges, in sequence.
 *
 * Rendered identically on the roster and the client page, so the two can no longer drift apart -- which
 * is the whole point. Nothing at all when there is nothing to say: a subscription quietly working is not
 * news, and a permanent "Active" badge would train the coach to stop reading this row.
 */
export function SubscriptionState({
  client,
  sequence,
}: {
  client: Pick<CoachOverviewClient, "billing_status" | "cancel_at" | "pending_inactive_at" | "status">;
  sequence?: SubscriptionSequence;
}) {
  const lines = describeSubscriptionSequence(client, sequence);
  if (lines.length === 0) return null;

  return (
    <>
      {lines.map((line) => (
        <span key={line} className="badge badge-warn">
          {line}
        </span>
      ))}
    </>
  );
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { month: "long", day: "numeric" });
}
